import React, { Component } from "react";
import PropTypes from "prop-types";
import { select, event as d3Event, mouse, touches } from "d3-selection";
import { mean } from "d3-array";

import {
  first,
  last,
  isDefined,
  noop,
  mousePosition,
  d3Window,
  MOUSEMOVE,
  MOUSEUP,
  TOUCHMOVE,
  TOUCHEND,
  touchPosition,
  getTouchProps,
  sign,
} from "../utils";

class AxisZoomCapture extends Component {
  constructor(props) {
    super(props);
    this.handleDragStartMouse = this.handleDragStartMouse.bind(this);
    this.handleDragStartTouch = this.handleDragStartTouch.bind(this);
    this.handleDrag = this.handleDrag.bind(this);
    this.handleDragEnd = this.handleDragEnd.bind(this);
    this.handleRightClick = this.handleRightClick.bind(this);
    this.saveNode = this.saveNode.bind(this);

    this.state = {
      startPosition: null,
    };

    // FIX #2: Track last mouse position for incremental deltas (like chart separator)
    this.lastMouseY = null;
  }
  saveNode(node) {
    this.node = node;
  }

  // Optimization: Only re-render when visual properties change
  shouldComponentUpdate(nextProps, nextState) {
    // Re-render if drag state changed
    if (this.state.startPosition !== nextState.startPosition) {
      return true;
    }

    // Re-render if rect position/size changed
    const { bg } = this.props;
    const nextBg = nextProps.bg;
    if (
      bg.x !== nextBg.x ||
      bg.y !== nextBg.y ||
      bg.h !== nextBg.h ||
      bg.w !== nextBg.w
    ) {
      return true;
    }

    // Skip re-render for data changes
    return false;
  }
  handleRightClick(e) {
    e.stopPropagation();
    e.preventDefault();

    const { onContextMenu } = this.props;

    const mouseXY = mousePosition(e, this.node.getBoundingClientRect());

    select(d3Window(this.node)).on(MOUSEMOVE, null).on(MOUSEUP, null);
    this.setState({
      startPosition: null,
    });

    onContextMenu(mouseXY, e);

    this.contextMenuClicked = true;
  }
  handleDragStartMouse(e) {
    this.mouseInteraction = true;

    const { getScale, getMoreProps, axisZoomStartCallback } = this.props;
    const moreProps = getMoreProps();

    // Use a fallback if moreProps is null
    if (!moreProps) {
      return;
    }

    const startScale = getScale(moreProps);
    this.dragHappened = false;

    if (startScale && startScale.invert) {
      select(d3Window(this.node))
        .on(MOUSEMOVE, this.handleDrag, false)
        .on(MOUSEUP, this.handleDragEnd, false);

      const startXY = mousePosition(e);

      // FIX #2: Initialize lastMouseY for incremental tracking
      this.lastMouseY = startXY[1];

      // FIX #2: Call start callback (like chart separator)
      if (axisZoomStartCallback) {
        axisZoomStartCallback();
      }

      this.setState({
        startPosition: {
          startXY,
          startScale,
        },
      });
    }
    e.preventDefault();
  }
  handleDragStartTouch(e) {
    this.mouseInteraction = false;

    const { getScale, getMoreProps, axisZoomStartCallback } = this.props;
    const moreProps = getMoreProps();
    const startScale = getScale(moreProps);
    this.dragHappened = false;

    if (e.touches.length === 1 && startScale && startScale.invert) {
      select(d3Window(this.node))
        .on(TOUCHMOVE, this.handleDrag)
        .on(TOUCHEND, this.handleDragEnd);

      const startXY = touchPosition(getTouchProps(e.touches[0]), e);

      // FIX #2: Initialize lastMouseY for incremental tracking
      this.lastMouseY = startXY[1];

      // FIX #2: Call start callback (like chart separator)
      if (axisZoomStartCallback) {
        axisZoomStartCallback();
      }

      this.setState({
        startPosition: {
          startXY,
          startScale,
        },
      });
    }
  }
  handleDrag() {
    const { startPosition } = this.state;
    const {
      getMouseDelta,
      inverted,
      axisZoomUpdateCallback,
      axisZoomCallback,
    } = this.props;

    this.dragHappened = true;
    if (isDefined(startPosition)) {
      const { startScale } = startPosition;
      const { startXY } = startPosition;

      const mouseXY = this.mouseInteraction
        ? mouse(this.node)
        : touches(this.node)[0];

      const diff = getMouseDelta(startXY, mouseXY);

      const center = mean(startScale.range());

      const tempRange = startScale
        .range()
        .map((d) =>
          inverted ? d - sign(d - center) * diff : d + sign(d - center) * diff,
        );

      const newDomain = tempRange.map(startScale.invert);

      if (
        sign(last(startScale.range()) - first(startScale.range())) ===
        sign(last(tempRange) - first(tempRange))
      ) {
        // FIX #2: Use update callback if available (temporary state)
        if (axisZoomUpdateCallback) {
          // Update callback expects only new domain
          axisZoomUpdateCallback(newDomain);
        } else {
          // Fallback to old callback (for backward compatibility)
          axisZoomCallback(newDomain);
        }
      }
    }
  }
  handleDragEnd() {
    const { axisZoomCommitCallback } = this.props;

    if (!this.dragHappened) {
      if (this.clicked) {
        const e = d3Event;
        const mouseXY = this.mouseInteraction
          ? mouse(this.node)
          : touches(this.node)[0];
        const { onDoubleClick } = this.props;

        onDoubleClick(mouseXY, e);
      } else {
        this.clicked = true;
        setTimeout(() => {
          this.clicked = false;
        }, 300);
      }
    } else {
      // FIX #2: Call commit callback on drag end (like chart separator)
      if (axisZoomCommitCallback) {
        axisZoomCommitCallback();
      }
    }

    // FIX #2: Clear last mouse position
    this.lastMouseY = null;

    select(d3Window(this.node))
      .on(MOUSEMOVE, null)
      .on(MOUSEUP, null)
      .on(TOUCHMOVE, null)
      .on(TOUCHEND, null);

    this.setState({
      startPosition: null,
    });
  }
  render() {
    const { bg, className, zoomCursorClassName } = this.props;

    const cursor = isDefined(this.state.startPosition)
      ? zoomCursorClassName
      : "react-stockcharts-default-cursor";

    return (
      <rect
        className={`react-stockcharts-enable-interaction ${cursor} ${className}`}
        ref={this.saveNode}
        x={bg.x}
        y={bg.y}
        opacity={0}
        height={bg.h}
        width={bg.w}
        onContextMenu={this.handleRightClick}
        onMouseDown={this.handleDragStartMouse}
        onTouchStart={this.handleDragStartTouch}
      />
    );
  }
}

AxisZoomCapture.propTypes = {
  innerTickSize: PropTypes.number,
  outerTickSize: PropTypes.number,
  tickFormat: PropTypes.func,
  tickPadding: PropTypes.number,
  tickSize: PropTypes.number,
  ticks: PropTypes.number,
  tickValues: PropTypes.array,
  showDomain: PropTypes.bool,
  showTicks: PropTypes.bool,
  className: PropTypes.string,
  axisZoomCallback: PropTypes.func,
  axisZoomStartCallback: PropTypes.func,
  axisZoomUpdateCallback: PropTypes.func,
  axisZoomCommitCallback: PropTypes.func,
  inverted: PropTypes.bool,
  bg: PropTypes.object.isRequired,
  zoomCursorClassName: PropTypes.string.isRequired,
  getMoreProps: PropTypes.func.isRequired,
  getScale: PropTypes.func.isRequired,
  getMouseDelta: PropTypes.func.isRequired,
  onDoubleClick: PropTypes.func.isRequired,
  onContextMenu: PropTypes.func.isRequired,
};

AxisZoomCapture.defaultProps = {
  onDoubleClick: noop,
  onContextMenu: noop,
  inverted: true,
};

export default AxisZoomCapture;
